import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useBulkUpdate, useProducts, useStores, useUpdateProduct } from '../../lib/admin'
import { count } from '../../lib/format'
import type { AdminProduct } from '../../lib/admintypes'
import { useSlots } from '../../lib/workspace'

import { AdminLayout } from './AdminLayout'
import { ProductEntry } from './ProductEntry'

/**
 * The whitelist (06 §6) — the screen the whole product hangs off.
 *
 * A crawl leaves tens of thousands of rows. None of them can reach a homeowner until somebody
 * approves them, so this screen has one job: **make approving fifty products take two minutes,
 * not an afternoon.** Everything here follows from that.
 *
 *  * The filters that matter are the ones that produce a batch worth bulk-approving:
 *    "not approved", "needs a slot", "price gone stale".
 *  * Select-all applies to the page, and bulk actions say how many rows they will touch.
 *  * Assigning a slot is the same action as approving, because a product with no slot is
 *    invisible to the rail whether it is approved or not.
 *
 * Approving queues two jobs — the embedding, so the rail can find it in the same minute, and a
 * one-off product-page fetch to fill in the full specification. That is what pays for the cheap
 * listing-only crawl (06 §4).
 */

export function AdminProducts() {
  const stores = useStores()
  const slots = useSlots()
  const update = useUpdateProduct()
  const bulk = useBulkUpdate()

  // Seeded from the URL, and kept there.
  //
  // The Overview screen has always linked here as `/admin/products?slot=bathroom.vanity`, and
  // these filters have always been plain `useState` — so every one of those links landed on an
  // unfiltered list. Andrew clicks "Vanity" on a coverage table to go and fix the vanities, and
  // gets 2,186 products. Reading the query string is the fix; writing it back is what makes a
  // filtered view something he can bookmark or send to somebody.
  const [params, setParams] = useSearchParams()
  const [q, setQ] = useState(params.get('q') ?? '')
  const [draft, setDraft] = useState(params.get('q') ?? '')
  const [store, setStore] = useState(params.get('store') ?? '')
  const [slot, setSlot] = useState(params.get('slot') ?? '')
  const [view, setView] = useState<'pending' | 'approved' | 'needs_slot' | 'stale' | 'all'>(
    // A link that names a slot or a store is asking "what is in here", not "what is unapproved
    // in here" — landing on the `pending` tab would hide everything already approved.
    (params.get('view') as 'pending' | 'approved' | 'needs_slot' | 'stale' | 'all' | null) ??
      (params.get('slot') || params.get('store') ? 'all' : 'pending'),
  )
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkSlot, setBulkSlot] = useState('')

  const filters = useMemo(
    () => ({
      q: q || undefined,
      store: store || undefined,
      slot: slot || undefined,
      whitelisted: view === 'approved' ? true : view === 'pending' ? false : undefined,
      needs_slot: view === 'needs_slot',
      stale: view === 'stale',
      offset,
      limit: 50,
    }),
    [q, store, slot, view, offset],
  )
  // `replace`, not push: filtering is not navigation, and a back button that walks through
  // eleven filter states is worse than one that leaves the screen.
  useEffect(() => {
    const next = new URLSearchParams()
    if (q) next.set('q', q)
    if (store) next.set('store', store)
    if (slot) next.set('slot', slot)
    if (view !== 'pending') next.set('view', view)
    if (next.toString() !== params.toString()) setParams(next, { replace: true })
  }, [q, store, slot, view, params, setParams])

  const products = useProducts(filters)
  const rows = products.data?.products ?? []
  const total = products.data?.total ?? 0

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.id))

  async function runBulk(body: Record<string, unknown>) {
    if (selected.size === 0) return
    await bulk.mutateAsync({ product_ids: [...selected], ...body })
    setSelected(new Set())
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl">Products</h1>
        <p className="m-0 font-mono text-xs uppercase tracking-[0.12em] text-muted">
          {count(total)} matching
        </p>
      </div>
      <p className="mt-2 max-w-[70ch] text-sm text-muted">
        Nothing a homeowner sees exists until you approve it here. A product with no slot is
        invisible even once approved — the slot is what puts it in a rail.
      </p>

      <ProductEntry stores={stores.data ?? []} />

      {/* ------------------------------------------------------------- filters */}
      <div className="mt-5 flex flex-wrap items-end gap-2">
        {/* wrap, not nowrap: five filter chips are ~500 px and this screen is used on a phone
            in a supplier's yard as often as at a desk. */}
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              ['pending', 'Not approved'],
              ['needs_slot', 'Needs a slot'],
              ['approved', 'Approved'],
              ['stale', 'Stale price'],
              ['all', 'All'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={view === value}
              onClick={() => {
                setView(value)
                setOffset(0)
              }}
              className={`min-h-11 rounded-pill border px-3.5 text-sm font-semibold transition-colors ${
                view === value
                  ? 'border-navy bg-navy text-white'
                  : 'border-line bg-white text-ink hover:border-blue'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          className="flex w-full min-w-0 gap-2 sm:w-auto sm:flex-none"
          onSubmit={(event) => {
            event.preventDefault()
            setQ(draft.trim())
            setOffset(0)
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Search names…"
            aria-label="Search product names"
            className="min-h-11 w-full min-w-0 rounded-btn border border-line bg-white px-3 text-sm text-ink sm:w-56"
          />
          <button
            type="submit"
            className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep"
          >
            Search
          </button>
        </form>

        <label className="min-w-0 max-w-full text-xs text-muted">
          <span className="block pb-1">Store</span>
          <select
            value={store}
            onChange={(event) => {
              setStore(event.target.value)
              setOffset(0)
            }}
            className="min-h-11 w-full max-w-[16rem] rounded-btn border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">All stores</option>
            {(stores.data ?? []).map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>

        <label className="min-w-0 max-w-full text-xs text-muted">
          <span className="block pb-1">Slot</span>
          <select
            value={slot}
            onChange={(event) => {
              setSlot(event.target.value)
              setOffset(0)
            }}
            className="min-h-11 w-full max-w-[16rem] rounded-btn border border-line bg-white px-3 text-sm text-ink"
          >
            <option value="">Any slot</option>
            {(slots.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* ---------------------------------------------------------- bulk actions */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 mt-4 flex flex-wrap items-center gap-2 rounded-card border border-blue bg-blue-tint px-4 py-3">
          <strong className="text-sm text-ink">{selected.size} selected</strong>
          <button
            type="button"
            onClick={() => void runBulk({ whitelisted: true })}
            className="min-h-10 rounded-btn bg-blue px-4 text-sm font-semibold text-white"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={() => void runBulk({ whitelisted: false })}
            className="min-h-10 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-press"
          >
            Un-approve
          </button>
          <button
            type="button"
            onClick={() => void runBulk({ preferred: true })}
            className="min-h-10 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-ink"
          >
            Mark recommended
          </button>
          <label className="ml-auto flex items-center gap-2 text-xs text-muted">
            <span>Add slot</span>
            <select
              value={bulkSlot}
              onChange={(event) => setBulkSlot(event.target.value)}
              className="min-h-10 rounded-btn border border-line bg-white px-2 text-sm text-ink"
            >
              <option value="">Choose…</option>
              {(slots.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!bulkSlot}
              onClick={() => void runBulk({ add_slots: [bulkSlot] })}
              className="min-h-10 rounded-btn bg-navy px-3 text-sm font-semibold text-white disabled:opacity-40"
            >
              Apply
            </button>
          </label>
        </div>
      )}

      {/* ---------------------------------------------------------------- rows */}
      {products.isPending && <p className="mt-8 text-muted">Loading…</p>}
      {!products.isPending && rows.length === 0 && (
        <p className="mt-8 rounded-card border border-line bg-white p-6 text-muted">
          Nothing matches those filters.
        </p>
      )}

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded-card border border-line bg-white">
          {/* Explicit column widths. Auto layout gives the long product name almost everything
              and squeezes the slot control to about 40 px — which renders as "Flo…", a control
              whose whole job is to tell Andrew what a product is classified as. */}
          <table className="w-full min-w-[980px] table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-11" />
              <col />
              <col className="w-[8.5rem]" />
              <col className="w-[15rem]" />
              <col className="w-[13rem]" />
            </colgroup>
            <thead>
              <tr className="border-b border-line text-left">
                <th className="p-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    aria-label="Select every product on this page"
                    onChange={(event) =>
                      setSelected(
                        event.target.checked ? new Set(rows.map((row) => row.id)) : new Set(),
                      )
                    }
                    className="size-4"
                  />
                </th>
                <th className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted">
                  Product
                </th>
                <th className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted">
                  Price
                </th>
                <th className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted">
                  Slot
                </th>
                <th className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted">
                  State
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  product={row}
                  slots={slots.data ?? []}
                  selected={selected.has(row.id)}
                  onToggle={() => toggle(row.id)}
                  onUpdate={(body) => void update.mutateAsync({ id: row.id, ...body })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 50 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
            className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-ink disabled:opacity-40"
          >
            Previous
          </button>
          <span className="font-mono text-xs text-muted">
            {offset + 1}–{Math.min(offset + 50, total)} of {count(total)}
          </span>
          <button
            type="button"
            disabled={offset + 50 >= total}
            onClick={() => setOffset(offset + 50)}
            className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-ink disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  )
}

function Row({
  product,
  slots,
  selected,
  onToggle,
  onUpdate,
}: {
  product: AdminProduct
  slots: { id: string; name: string }[]
  selected: boolean
  onToggle: () => void
  onUpdate: (body: Record<string, unknown>) => void
}) {
  // The mirrored copy, not the store CDN: `img-src` allows studio-files and nothing else.
  const image = product.image_url
  const stale = (product.price_age_days ?? 0) > 7

  return (
    <tr className="border-b border-line last:border-0 align-top">
      <td className="p-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${product.name}`}
          className="size-4"
        />
      </td>
      <td className="p-3">
        <div className="flex gap-3">
          {image ? (
            <img src={image} alt="" loading="lazy" className="size-14 rounded-btn object-cover" />
          ) : (
            <div className="grid size-14 place-items-center rounded-btn bg-mist text-xs text-muted">
              none
            </div>
          )}
          <div className="min-w-0">
            <a
              href={product.url}
              target="_blank"
              rel="noreferrer noopener"
              title={product.name}
              className="line-clamp-2 font-semibold text-ink underline-offset-2 hover:underline"
            >
              {product.name}
            </a>
            <p className="m-0 text-xs text-muted">
              {product.store_name}
              {product.brand ? ` · ${product.brand}` : ''}
              {product.product_type ? ` · ${product.product_type}` : ''}
            </p>
          </div>
        </div>
      </td>
      <td className="p-3 whitespace-nowrap">
        {product.price ? (
          <>
            <div className="font-semibold text-ink">
              ${product.price}{' '}
              <span className="text-xs font-normal text-muted">{product.unit}</span>
            </div>
            <div className={`text-xs ${stale ? 'text-blue-press' : 'text-muted'}`}>
              {product.price_age_days === null
                ? ''
                : `${Math.round(product.price_age_days)} d old${stale ? ' · unverified' : ''}`}
            </div>
          </>
        ) : (
          <span className="text-xs text-muted">no price</span>
        )}
      </td>
      <td className="p-3">
        <select
          value={product.slots[0] ?? ''}
          onChange={(event) => onUpdate({ slots: event.target.value ? [event.target.value] : [] })}
          aria-label={`Slot for ${product.name}`}
          className="min-h-10 w-full rounded-btn border border-line bg-white px-2 text-sm text-ink"
        >
          <option value="">— none —</option>
          {slots.map((slot) => (
            <option key={slot.id} value={slot.id}>
              {slot.name}
            </option>
          ))}
        </select>
      </td>
      <td className="p-3">
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => onUpdate({ whitelisted: !product.whitelisted })}
            className={`min-h-10 rounded-btn px-3 text-xs font-semibold ${
              product.whitelisted
                ? 'bg-blue text-white'
                : 'border border-line bg-white text-blue-deep'
            }`}
          >
            {product.whitelisted ? 'Approved' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={() => onUpdate({ preferred: !product.preferred })}
            aria-pressed={product.preferred}
            title="Recommend this one — it sorts to the top of the rail"
            // `relative`: Tailwind's `sr-only` is absolutely positioned, and without a
            // positioned ancestor it lands at its static position in the *viewport's*
            // coordinate space — which, inside a 900 px table, is 830 px off-screen and makes
            // the whole page scroll sideways on a phone. Costs nothing, and it is the bug that
            // took the longest to find.
            className={`relative grid size-11 place-items-center rounded-btn text-sm font-semibold ${
              product.preferred ? 'bg-navy text-white' : 'border border-line bg-white text-ink'
            }`}
          >
            <span aria-hidden="true">★</span>
            <span className="sr-only">
              {product.preferred ? 'Recommended' : 'Mark as recommended'}
            </span>
          </button>
        </div>
        {product.whitelisted && !product.has_embedding && (
          <p className="m-0 pt-1 text-xs text-muted">indexing…</p>
        )}
      </td>
    </tr>
  )
}
